import { useConnectedStoresQuery } from '@/hooks/queries/useConnectedStoresQuery';
import useApiUploadMutation from '@/hooks/mutations/useApiUploadMutation';
import { lazy, useCallback, useEffect, useState } from 'react';
import { EMPTY_ARRAY } from '@/constants/general';
import { toastApiError } from '@/utils/api';
import { useOpenModal } from '@/hooks/general/useOpenModal';
import { camelToSnakeCase } from '@/utils/snakeCamelTransform';
import { Store } from '@/types/store';
import { isStoreConnected } from '@app/pages/advanced/components/EmptyState/helpers';
import { toastSuccess } from '@app/components/Toastify';
import { useIntl } from 'react-intl';
import { useReports } from '../useReports';

const FallbackSettingsDialog = lazy(
  () => import('@/features/Stores/components/FallbackSettingsDialog')
);

const SCENARIO_NAME = 'Sync Orders Attempt';

const getSyncDates = () => {
  const createdBefore = new Date();
  const createdAfter = new Date();
  createdAfter.setDate(createdAfter.getDate() + 1);
  createdAfter.setMonth(createdAfter.getMonth() - 1);
  return {
    createdBefore,
    createdAfter,
  };
};

const needFallbackSettingDialog = (store: Store) =>
  store.missingRequiredFallback &&
  !store.requiresReauthorizePermission &&
  store.authState === 'verified';

export const useSyncAllStores = () => {
  const { formatMessage } = useIntl();
  const { refetch } = useConnectedStoresQuery();
  const openModal = useOpenModal();
  const [isSyncProcessing, setIsSyncProcessing] = useState(false);
  const { mutateAsync: mutateApiUpload } = useApiUploadMutation();
  const [reportStoreMap, setReportStoreMap] = useState<{ [reportId: string]: Store }>({});
  const { isComplete: isReportsComplete, isProcessing: isReportsProcessing } =
    useReports(reportStoreMap);

  const openModalForStore = useCallback(
    async (store: Store): Promise<boolean> => {
      return new Promise((resolve) => {
        const { close: closeModal } = openModal(FallbackSettingsDialog, {
          store: camelToSnakeCase(store),
          scenarioName: SCENARIO_NAME,
          dimensionsDisabled: true,
          onSuccess: async () => {
            closeModal?.();
            resolve(true);
          },
          onClose: () => {
            resolve(false);
          },
        });
      });
    },
    [openModal]
  );

  const syncStore = useCallback(
    async (store: Store) => {
      return mutateApiUpload({
        storeId: store.id,
        jobDescription: 'Manual Store Sync Advanced Page',
        ...getSyncDates(),
      });
    },
    [mutateApiUpload]
  );

  const syncSingleStore = useCallback(async () => {
    try {
      setIsSyncProcessing(true);
      const { data: stores = EMPTY_ARRAY } = await refetch();
      if (!stores.length) return;
      const store = stores[0];
      const isFallbackSettingsDialog = needFallbackSettingDialog(store);

      if (isFallbackSettingsDialog) {
        if (!(await openModalForStore(store))) return;
      }

      const response = await syncStore(stores[0]);
      setTimeout(() => {
        setReportStoreMap({ [response.report_id]: stores[0] });
      }, 1000);
    } catch (e) {
      toastApiError(e);
      setIsSyncProcessing(false);
    }
  }, [refetch, syncStore, openModalForStore]);

  const syncAllStores = useCallback(async () => {
    try {
      setIsSyncProcessing(true);
      const { data: stores = EMPTY_ARRAY } = await refetch();

      const connectedStores = stores.filter(isStoreConnected);
      const storesNeedFallback = connectedStores.filter(needFallbackSettingDialog);

      // eslint-disable-next-line no-restricted-syntax
      for (const store of storesNeedFallback) {
        // eslint-disable-next-line no-await-in-loop
        if (!(await openModalForStore(store))) return;
      }

      const responses = await Promise.all(connectedStores.map(syncStore));
      const reportIds = responses.map(({ report_id }) => report_id);

      toastSuccess(
        formatMessage({ id: 'reports.store-orders-fetching' }, { count: connectedStores.length })
      );

      setTimeout(() => {
        setReportStoreMap(
          reportIds.reduce((acc, reportId, index) => {
            acc[reportId] = connectedStores[index];
            return acc;
          }, {} as typeof reportStoreMap)
        );
      }, 1000);
    } catch (e) {
      toastApiError(e);
      setIsSyncProcessing(false);
    }
  }, [formatMessage, openModalForStore, refetch, syncStore]);

  useEffect(() => {
    if (isSyncProcessing && !isReportsProcessing && isReportsComplete) setIsSyncProcessing(false);
  }, [isReportsComplete, isReportsProcessing, isSyncProcessing]);

  return {
    syncSingleStore,
    syncAllStores,
    isSyncProcessing,
  };
};
